import React from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * PermissionGuard - Conditionally renders children based on user permissions
 * 
 * @param {string|string[]} permission - Single permission or array of permissions
 * @param {boolean} requireAll - If true, requires ALL permissions. If false, requires ANY permission (default: false)
 * @param {React.ReactNode} children - Content to render if permission check passes
 * @param {React.ReactNode} fallback - Content to render if permission check fails
 */
export const PermissionGuard = ({ 
  permission, 
  requireAll = false, 
  children, 
  fallback = null 
}) => {
  const { hasPermission, hasAnyPermission } = useAuth();

  // Handle single permission
  if (typeof permission === 'string') {
    return hasPermission(permission) ? children : fallback;
  }

  // Handle multiple permissions
  if (Array.isArray(permission)) {
    if (requireAll) {
      // Require ALL permissions
      const hasAllPermissions = permission.every(perm => hasPermission(perm));
      return hasAllPermissions ? children : fallback;
    } else {
      // Require ANY permission
      return hasAnyPermission(permission) ? children : fallback;
    }
  }

  // Invalid permission prop
  console.warn('PermissionGuard: permission prop must be a string or array of strings');
  return fallback;
};

/**
 * PermissionButton - Button that is only visible/enabled based on permissions
 */
export const PermissionButton = ({ 
  permission, 
  requireAll = false, 
  hideIfNoPermission = false,
  children, 
  ...buttonProps 
}) => {
  const { hasPermission, hasAnyPermission } = useAuth();

  let hasRequiredPermission = false;

  if (typeof permission === 'string') {
    hasRequiredPermission = hasPermission(permission);
  } else if (Array.isArray(permission)) {
    hasRequiredPermission = requireAll 
      ? permission.every(perm => hasPermission(perm))
      : hasAnyPermission(permission);
  }

  if (hideIfNoPermission && !hasRequiredPermission) {
    return null;
  }

  return (
    <button 
      {...buttonProps} 
      disabled={!hasRequiredPermission || buttonProps.disabled}
      className={`${buttonProps.className || ''} ${!hasRequiredPermission ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

/**
 * PermissionLink - Link that is only visible/enabled based on permissions
 */
export const PermissionLink = ({ 
  permission, 
  requireAll = false, 
  hideIfNoPermission = false,
  children, 
  ...linkProps 
}) => {
  const { hasPermission, hasAnyPermission } = useAuth();

  let hasRequiredPermission = false;

  if (typeof permission === 'string') {
    hasRequiredPermission = hasPermission(permission);
  } else if (Array.isArray(permission)) {
    hasRequiredPermission = requireAll 
      ? permission.every(perm => hasPermission(perm))
      : hasAnyPermission(permission);
  }

  if (hideIfNoPermission && !hasRequiredPermission) {
    return null;
  }

  return (
    <a 
      {...linkProps}
      className={`${linkProps.className || ''} ${!hasRequiredPermission ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
      onClick={!hasRequiredPermission ? (e) => e.preventDefault() : linkProps.onClick}
    >
      {children}
    </a>
  );
};

export default PermissionGuard;